<template>
  <action-dispatcher action="product/getProduct" :parameters="$route.params.id">
    <h2>{{ editing ? "Editar plano" : "Novo plano" }}</h2>

    <div class="d-flex justify-content-center my-9" v-if="loading">
      <b-spinner variant="primary" />
    </div>

    <div v-else>
      <bp-input
        v-model="form.name"
        id="name"
        required
        class="mt-7"
        label="Nome do plano"
        placeholder="Ex: Plano Mensal"
        description="Este nome será exibido para os compradores."
        :errors="errors.name"
      />
      <bp-input
        v-model="form.description"
        id="description"
        required
        class="mt-5"
        textarea
        :limit="980"
        label="Descrição no checkout"
        placeholder="Explique de forma breve o que está incluso neste plano"
        :errors="errors.description"
      />

      <bp-input
        v-model="form.sck"
        id="sck"
        class="mt-5"
        label="SCK"
        placeholder="Digite o parâmetro desejado"
        description="Esse parâmetro te ajudará a identificar a origem da compra."
      />

      <hr class="my-6" />

      <h3>Precificação</h3>

      <bp-input
        v-model="form.price"
        money
        :moneyConfig="moneyConfig"
        class="my-5"
        id="price"
        preIcon="fa-light fa-brazilian-real-sign"
        label="Valor em Real Brasileiro (R$)"
        placeholder="Informe o preço"
        :errors="errors.price"
        required
        description="Defina quanto custará o seu produto. Certifique-se de que o valor não seja menor que R$ 4,00."
        :min="4"
      />
      <bp-input
        v-model="form.price_usd"
        money
        id="price"
        class="my-5"
        preIcon="fa-light fa-dollar"
        placeholder="Informe o preço"
        label="Valor em Dólar Americano (US$)"
        description="Defina quanto custará o seu produto. Certifique-se de que o valor não seja menor que US$ 4,00."
        :min="4"
        :info="getInfo('USD')"
        :disabled="!showForeign"
        :errors="errors.price_usd"
        :moneyConfig="{ ...moneyConfig, currency: '$' }"
      />
      <bp-input
        v-model="form.price_eur"
        money
        id="price"
        class="my-5"
        label="Valor em Euro (€)"
        preIcon="fa-light fa-euro"
        placeholder="Informe o preço"
        description="Defina quanto custará o seu produto. Certifique-se de que o valor não seja menor que € 4,00."
        :min="4"
        :info="getInfo('EUR')"
        :disabled="!showForeign"
        :errors="errors.price_eur"
        :moneyConfig="{ ...moneyConfig, currency: '€' }"
      />

      <bp-select
        v-model="form.periodicity"
        id="frequency"
        class="mt-6"
        required
        select
        :disabled="!!form.id"
        label="Periodicidade"
        placeholder="Selecione a periodicidade..."
        :errors="errors.periodicity"
        :options="frequencyOptions"
        description="Escolha quando deverá ocorrer a renovação da assinatura."
      />

      <action-dispatcher
        v-if="form.price && frequencyMonths"
        action="sale/getInstallments"
        :parameters="installmentsParams"
        :key="`${frequencyMonths}${form.price}`"
      >
        <bp-select
          v-model="form.installments"
          id="installment"
          class="mt-6"
          select
          required
          label="Parcelamento"
          placeholder="Selecionar..."
          :errors="errors.installments"
          :options="installmentOptions"
          description="Para parcelar, a periodicidade da assinatura deve ser pelo menos bimestral."
        />
      </action-dispatcher>

      <bp-input
        v-model="form.test_days"
        id="test_days"
        class="mt-6 mb-2"
        mask="###"
        :disabled="isTwoCards"
        label="Dias de teste grátis"
        placeholder="0"
        :errors="errors.test_days"
      />

      <div v-if="isTwoCards" class="text-negative-600 fs-14">
        Opção desabilitada para assinaturas que aceitem pagamento com dois
        cartões.
      </div>
      <div v-else class="text-neutral-600 fs-14">
        Defina quantos dias de teste grátis o cliente terá antes da primeira
        cobrança. <br />
        Transações de período de teste terão cobrança automática de R$ 1,00.
        Caso esteja tudo certo com a transação, o estorno é feito
        automaticamente, mantendo o período de teste estipulado.
      </div>

      <bp-switch
        v-model="promotionalPrice"
        label="Novo preço depois do primeiro pagamento"
        class="mt-6"
      />

      <bp-input
        v-if="promotionalPrice"
        v-model="form.next_cycle_price"
        id="next_cycle_price"
        class="mt-5"
        required
        money
        :moneyConfig="{ prefix: 'R$ ' }"
        label="Novo preço"
        placeholder="R$ 0,00"
        :errors="errors.next_cycle_price"
        description="Defina qual será o novo valor a ser cobrado no seu plano, certificando-se que não seja um valor menor que R$4,00."
      />

      <hr class="my-6" />

      <bp-radio
        v-model="form.billing"
        id="billing"
        class="mt-5 mb-5"
        required
        label="Forma de cobrança"
        :errors="errors.billing"
        :options="billingOptions"
        vertical
      />

      <bp-input
        v-if="form.billing === 2"
        v-model="form.num_max_payments"
        id="num_max_payments"
        number
        required
        label="Recorrências"
        placeholder="0"
        :errors="errors.name"
        :min="1"
      />

      <div class="d-none d-lg-flex justify-content-between py-9">
        <bp-button
          @click="$router.push({ name: 'product-plans' })"
          color="neutral"
          variant="filled-1"
          label="Cancelar"
        />
        <bp-button
          @click="submit"
          color="primary"
          :disabled="!isValid"
          label="Salvar"
          :loading="loadingSubmit"
        />
      </div>

      <div class="d-lg-none py-6">
        <bp-button
          @click="$router.push({ name: 'product-plans' })"
          color="neutral"
          variant="filled-1"
          label="Cancelar"
        />

        <button
          @click="submit"
          class="btn btn-block btn-primary btn-primary"
          :disabled="!isValid"
        >
          <loading :show="loadingSubmit">
            Salvar
          </loading>
        </button>
      </div>
    </div>
  </action-dispatcher>
</template>

<script>
import { clearObject, formatMoney, formFields } from "@/functions/helpers";
import get from "lodash/get";
import { mapState } from "vuex";

export default {
  data() {
    return {
      status: false,
      loading: false,
      loadingSubmit: false,
      promotionalPrice: false,
      ...formFields([
        "id",
        "name",
        "description",
        "price",
        "price_usd",
        "price_eur",
        "periodicity",
        "installments",
        "testing",
        "test_days",
        "next_cycle_price",
        "billing",
        "num_max_payments",
        "payment_form",
        "status",
        "sck"
      ]),
      billingOptions: [
        { id: 1, name: "Até o cliente cancelar" },
        { id: 2, name: "Definir um número máximo" }
      ],
      moneyConfig: {
        decimal: ",",
        thousands: ".",
        precision: 2,
        prefix: "",
        masked: false
      }
    };
  },
  watch: {
    promotionalPrice(v) {
      if (!v) this.form.next_cycle_price = "";
    },
    "form.billing"(v) {
      if (v === 1) {
        this.form.num_max_payments = 0;
      }
    }
  },
  computed: {
    ...mapState("product", ["product", "frequencyOptions"]),
    ...mapState("sale", ["installments"]),
    ...mapState("plan", ["plan"]),
    editing() {
      return Object.keys(this.$route.params).includes("plan_id");
    },
    isTwoCards() {
      return !!get(this.product, "payment_methods", []).find(
        i => i?.code === "two_credit_card"
      );
    },
    installmentsParams() {
      return clearObject({
        installments: this.frequencyMonths,
        product_id: this.$route.params.id,
        value: this.form.price
      });
    },
    isValid() {
      return (
        this.form.name &&
        this.form.description &&
        this.form.description.length <= 980 &&
        this.form.price &&
        this.form.price >= 4 &&
        (!parseFloat(this.form.price_eur) || this.form.price_eur >= 4) &&
        (!parseFloat(this.form.price_usd) || this.form.price_usd >= 4) &&
        this.form.periodicity &&
        this.form.billing &&
        this.form.installments &&
        (this.form.billing === 1 || Number(this.form.num_max_payments)) &&
        (this.form.next_cycle_price >= 4 || !this.promotionalPrice)
      );
    },
    frequencyMonths() {
      if (!this.form.periodicity) {
        return 0;
      }
      if (["weekly", "monthly"].includes(this.form.periodicity)) {
        return 1;
      }
      return this.frequencyOptions.find(
        item => item.id === this.form.periodicity
      ).months;
    },
    installmentOptions() {
      const opts = [];
      for (let id in this.installments) {
        opts.push({
          id: parseInt(id),
          name: `Até ${id} parcela${id - 1 ? "s" : ""} de R$ ${formatMoney(
            this.installments[id]
          )}`
        });
      }
      return opts;
    },
    showForeign() {
      return this.product.payment_methods?.some(({ id }) => id === 1);
    }
  },
  methods: {
    getInfo(currency) {
      if (this.showForeign) return "";
      const currencyName = {
        USD: "dólar americano",
        EUR: "euro"
      };
      return `Para habilitar configuração de preço em ${currencyName[currency]} seu checkout precisa aceitar Cartão de Crédito. Atualize a configuração de "Opções de Pagamento" do seu produto em "Configurar Checkout"`;
    },
    submit() {
      this.loadingSubmit = true;

      const data = {
        ...this.form,
        status: this.status || !this.editing ? "active" : "inactive",
        product_id: this.$route.params.id
      };

      data.price_eur = parseFloat(data.price_eur) === 0 ? "" : data.price_eur;
      data.price_usd = parseFloat(data.price_usd) === 0 ? "" : data.price_usd;

      const action = this.editing ? "plan/put" : "plan/post";

      this.$store
        .dispatch(action, data)
        .then(() => {
          this.$message.success("Plano salvo com sucesso");
          this.$router.push({
            name: "product-plans",
            params: { id: this.$route.params.id }
          });
        })
        .catch(error => {
          this.errors = error.response.data;
          this.$message.error("Erro ao salvar plano");
        })
        .finally(() => (this.loadingSubmit = false));
    }
  },
  mounted() {
    if (this.$route.params.plan_id) {
      this.loading = true;
      this.$store
        .dispatch("plan/getPlan", this.$route.params.plan_id)
        .then(data => {
          this.promotionalPrice = !!parseInt(data.next_cycle_price);
          this.status = data.status === "active";
          this.form = {
            ...this.form,
            ...data,
            next_cycle_price: data.next_cycle_price || 0
          };
          this.form.billing = data.num_max_payments ? 2 : 1;
        })
        .finally(() => (this.loading = false));
    }
  }
};
</script>
